import React, { useState, useEffect } from 'react';
import { X, Download, Share2, AlertCircle, FileText, ExternalLink, Info, CheckCircle } from 'lucide-react';
import Button from './ui/Button';

const OfficeOnlineViewer = ({ document: documentData, onClose, mode = 'view' }) => {
  const [error, setError] = useState(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [showInfoPanel, setShowInfoPanel] = useState(true);

  // Add ESC key support to close modal
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event?.key === 'Escape' && onClose) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscKey);
    
    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [onClose]);

  useEffect(() => {
    if (documentData) {
      // Always show info panel explaining Office Online limitation
      setShowInfoPanel(true);
    }
  }, [documentData]);

  // Handle backdrop click to close modal
  const handleBackdropClick = (e) => {
    // Only close if clicking the backdrop itself, not the modal content
    if (e?.target === e?.currentTarget && onClose) {
      onClose();
    }
  };

  // Handle close button click
  const handleCloseClick = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (onClose) {
      onClose();
    }
  };

  const handleDownload = async () => {
    try {
      setIsDownloading(true);
      
      // Get document URL from storage
      let documentUrl = documentData?.storage_path || documentData?.url || documentData?.filePath;
      
      if (!documentUrl) {
        setError('Document URL not available. Please try again from the document management page.');
        return;
      }
      
      // Create temporary link and trigger download
      const link = document.createElement('a');
      link.href = documentUrl;
      link.download = documentData?.name || 'document';
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      
      // Append to body, click, and remove
      document.body?.appendChild(link);
      link?.click();
      document.body?.removeChild(link);
      
      // Show success message
      setError(null);
      setShowInfoPanel(false);
      
      // Close viewer after download starts
      setTimeout(() => {
        onClose?.();
      }, 2000);
      
    } catch (err) {
      console.error('Download error:', err);
      setError(`Unable to download document: ${err?.message}. Please try accessing the document from the file manager.`);
    } finally {
      setIsDownloading(false);
    }
  };

  const handleShare = () => {
    const documentUrl = documentData?.storage_path || documentData?.url || documentData?.filePath;
    
    if (!documentUrl) {
      setError('Document URL not available for sharing.');
      return;
    }
    
    if (navigator?.share) {
      navigator.share({
        title: documentData?.name,
        text: `Check out this document: ${documentData?.name}`,
        url: documentUrl,
      })?.catch(err => {
        if (err?.name !== 'AbortError') {
          console.log('Error sharing:', err);
          setError('Unable to share document. You can copy the link manually.');
        }
      });
    } else {
      // Fallback: copy link to clipboard
      navigator.clipboard?.writeText(documentUrl)
        ?.then(() => {
          alert('Document link copied to clipboard!');
        })
        ?.catch(err => {
          console.error('Copy failed:', err);
          alert('Unable to copy link. Please copy manually: ' + documentUrl);
        });
    }
  };

  const handleOpenInDesktopApp = () => {
    // Attempt to open with ms-office protocol handler
    const documentUrl = documentData?.storage_path || documentData?.url || documentData?.filePath;
    const extension = documentData?.name?.split('.')?.pop()?.toLowerCase();
    
    let protocolScheme = 'ms-word:';
    let appName = 'Word';
    
    switch (extension) {
      case 'docx':
      case 'doc':
        protocolScheme = 'ms-word:';
        appName = 'Word';
        break;
      case 'xlsx': case'xls':
        protocolScheme = 'ms-excel:';
        appName = 'Excel';
        break;
      case 'pptx': case'ppt':
        protocolScheme = 'ms-powerpoint:';
        appName = 'PowerPoint';
        break;
      default:
        protocolScheme = 'ms-word:';
        appName = 'Word';
    }
    
    // Try to open with protocol handler (only works if Office is installed)
    try {
      window.location.href = `${protocolScheme}ofe|u|${documentUrl}`;
      
      // Show message that desktop app should open
      setTimeout(() => {
        setError(`If Microsoft ${appName} didn't open automatically, the application may not be installed. Please download the document and open it manually.`);
      }, 2000);
    } catch (err) {
      setError(`Unable to open with Microsoft ${appName}. Please download the document and open it manually with Microsoft Office desktop application.`);
    }
  };

  if (!documentData) return null;

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
      onClick={handleBackdropClick}
    >
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center">
              <FileText className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">{documentData?.name}</h2>
              <p className="text-sm text-gray-600">Document Editing Options</p>
            </div>
          </div>
          
          <button
            type="button"
            onClick={handleCloseClick}
            className="flex items-center justify-center hover:bg-gray-200 rounded-full p-2 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
            aria-label="Close modal"
          >
            <X className="w-5 h-5 text-gray-700" />
          </button>
        </div>

        {/* Content Area */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Main Information Panel */}
          {showInfoPanel && (
            <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-6">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <Info className="w-8 h-8 text-blue-600" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-blue-900 mb-3">
                    About Office Online Integration
                  </h3>
                  <div className="space-y-3 text-sm text-blue-800">
                    <p className="leading-relaxed">
                      <strong>Office Online web viewer requires documents to be publicly accessible on the internet.</strong> Documents stored in your secure ZentriDMS system use private, authenticated storage and cannot be directly opened in Office Online's web viewer.
                    </p>
                    <div className="bg-white bg-opacity-50 rounded-lg p-4 border border-blue-200">
                      <p className="font-semibold mb-2">✨ Recommended Workflow:</p>
                      <ol className="list-decimal list-inside space-y-1 ml-2">
                        <li>Download the document to your computer</li>
                        <li>Open with Microsoft Office desktop application (Word, Excel, PowerPoint)</li>
                        <li>Edit and save your changes</li>
                        <li>Upload the updated version back to ZentriDMS</li>
                      </ol>
                    </div>
                    <p className="text-xs italic">
                      💡 This workflow ensures your documents remain secure while giving you full editing capabilities.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Error Display */}
          {error && !showInfoPanel && (
            <div className="bg-red-50 border-2 border-red-200 rounded-xl p-4">
              <div className="flex items-start space-x-3">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm text-red-800">{error}</p>
                </div>
              </div>
            </div>
          )}

          {/* Success Message (after download) */}
          {!showInfoPanel && !error && (
            <div className="bg-green-50 border-2 border-green-200 rounded-xl p-4">
              <div className="flex items-start space-x-3">
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm text-green-800 font-medium">
                    Download started successfully! The document will open in your downloads folder.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="space-y-3">
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl p-1">
              <Button
                variant="primary"
                className="w-full justify-center bg-white text-blue-600 hover:bg-gray-50 rounded-lg py-4 font-semibold text-base"
                onClick={handleDownload}
                disabled={isDownloading}
              >
                {isDownloading ? (
                  <>
                    <div className="inline-block animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600 mr-3"></div>
                    Downloading Document...
                  </>
                ) : (
                  <>
                    <Download className="w-5 h-5 mr-3" />
                    Download Document
                  </>
                )}
              </Button>
            </div>

            <Button
              variant="outline"
              className="w-full justify-center border-2 border-gray-300 hover:bg-gray-50 py-3 font-medium"
              onClick={handleOpenInDesktopApp}
              disabled={isDownloading}
            >
              <ExternalLink className="w-5 h-5 mr-2" />
              Try Opening in Desktop Office App
            </Button>

            <Button
              variant="outline"
              className="w-full justify-center border-2 border-gray-300 hover:bg-gray-50 py-3 font-medium"
              onClick={handleShare}
              disabled={isDownloading}
            >
              <Share2 className="w-5 h-5 mr-2" />
              Share Document Link
            </Button>
          </div>

          {/* Additional Information Section */}
          <div className="bg-gray-50 rounded-xl p-5 border border-gray-200">
            <h4 className="font-bold text-gray-900 mb-3 flex items-center">
              <FileText className="w-4 h-4 mr-2" />
              Why can't I edit directly in Office Online?
            </h4>
            <div className="text-sm text-gray-700 space-y-2">
              <p>
                <strong>Security First:</strong> ZentriDMS stores your documents in secure, private storage with authentication and access controls. This is essential for protecting sensitive business documents.
              </p>
              <p>
                <strong>Office Online Limitation:</strong> Microsoft's Office Online viewer can only access documents that are publicly available on the internet without authentication. This is a limitation of the Office Online service itself, not ZentriDMS.
              </p>
              <p>
                <strong>Best Practice:</strong> The download-edit-upload workflow provides the same editing experience while maintaining document security and compliance requirements.
              </p>
            </div>
          </div>

          {/* Alternative Solutions */}
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
            <h4 className="font-semibold text-amber-900 mb-3">
              🚀 Want Seamless Online Editing?
            </h4>
            <div className="text-sm text-amber-800 space-y-2">
              <p>
                For true online editing without downloads, consider these enterprise solutions:
              </p>
              <ul className="list-disc list-inside ml-2 space-y-1">
                <li><strong>Microsoft 365 SharePoint:</strong> Native Office Online integration</li>
                <li><strong>OnlyOffice:</strong> Self-hosted document collaboration platform</li>
                <li><strong>Collabora Online:</strong> Enterprise document server</li>
              </ul>
              <p className="text-xs italic mt-3">
                Contact your system administrator for more information about these options.
              </p>
            </div>
          </div>

          {/* Close Button */}
          <Button
            variant="ghost"
            className="w-full justify-center py-3 text-gray-600 hover:bg-gray-100"
            onClick={handleCloseClick}
          >
            Close
          </Button>
        </div>
      </div>
    </div>
  );
};

export default OfficeOnlineViewer;